import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link2, Loader2 } from "lucide-react";
import { WarmUpManualSendRule } from "@/pages/WarmUpSequences/WarmUpDeliveryStatus";
import { getClientAuthHeaders, getClientAuthToken } from "@/lib/apiAuth";

const ALREADY_ISSUED = "Warm Up workflow secret is already issued";
const INTEGRATION_UNAVAILABLE = "GHL integration is not available";

function backendUrl() {
  return (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
}

export function warmUpWorkflowWebhookUrl(integrationId) {
  const id = typeof integrationId === "string" ? integrationId.trim() : "";
  const base = backendUrl();
  if (!id || !base) return "";
  return `${base}/api/webhooks/ghl/warm-up-sequences/${id}/stage-changed`;
}

function safeWorkflowError(error) {
  const status = error?.response?.status;
  if (status === 404) return INTEGRATION_UNAVAILABLE;
  if (status === 409) return ALREADY_ISSUED;
  return "Could not update the Warm Up webhook secret.";
}

export default function WarmUpWorkflowSetup({
  integrationId = "",
  onSecretConfigured,
  sequencesLoading = false,
  triggerText = "",
  webhookSecretConfigured = false,
  pitKind = "loading",
  catalogStatus = "unavailable",
}) {
  const [secret, setSecret] = useState("");
  const [issuedUrl, setIssuedUrl] = useState("");
  const [error, setError] = useState("");
  const [alreadyIssued, setAlreadyIssued] = useState(false);
  const [pending, setPending] = useState(false);
  const [setupStepsOpen, setSetupStepsOpen] = useState(true);
  const setupStepsDefaultApplied = useRef(false);
  const webhookUrl = issuedUrl || warmUpWorkflowWebhookUrl(integrationId);

  useEffect(() => {
    if (setupStepsDefaultApplied.current) return;
    if (sequencesLoading || pitKind === "loading" || catalogStatus === "loading") return;
    if (pitKind === "ready" && catalogStatus === "unavailable") return;
    setupStepsDefaultApplied.current = true;
    setSetupStepsOpen(triggerText !== "Automatic stage triggers are ready.");
  }, [sequencesLoading, pitKind, catalogStatus, triggerText]);

  async function submit(rotate) {
    const token = getClientAuthToken();
    const base = backendUrl();
    if (!token || !base) {
      setError(INTEGRATION_UNAVAILABLE);
      return;
    }
    setPending(true);
    setError("");
    try {
      const res = await axios.post(
        `${base}/api/integrations/ghl/warm-up-webhook-secret`,
        rotate ? { rotate: true } : {},
        { headers: getClientAuthHeaders() }
      );
      const nextSecret = typeof res.data?.bearer_secret === "string" ? res.data.bearer_secret : "";
      const nextUrl = typeof res.data?.webhook_url === "string" ? res.data.webhook_url : "";
      if (!nextSecret) {
        setError("Could not update the Warm Up webhook secret.");
        return;
      }
      setSecret(nextSecret);
      if (nextUrl && !nextUrl.includes(nextSecret)) setIssuedUrl(nextUrl);
      setAlreadyIssued(false);
      if (onSecretConfigured) onSecretConfigured();
    } catch (requestError) {
      const message = safeWorkflowError(requestError);
      setError(message);
      if (message === ALREADY_ISSUED) {
        setSecret("");
        setAlreadyIssued(true);
        if (onSecretConfigured) onSecretConfigured();
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <>
      <section
        className="glass rounded-2xl p-6 border border-white/10 space-y-4 mb-6"
        data-testid="warm-up-workflow-setup"
      >
        <h2 className="text-[#F3F4F6] font-heading font-semibold text-lg flex items-center gap-2">
          <Link2 className="w-5 h-5 text-[#00D1FF]" />
          HighLevel workflow setup
        </h2>
        <details
          className="rounded-lg border border-white/10 bg-white/[0.03] p-3"
          open={setupStepsOpen}
          onToggle={(event) => setSetupStepsOpen(event.currentTarget.open)}
        >
          <summary className="cursor-pointer text-sm text-[#9CA3AF] font-body select-none">
            Setup steps
          </summary>
          <div
            className="mt-3 space-y-3 text-[#9CA3AF] text-sm font-body leading-relaxed"
            data-testid="warm-up-workflow-instructions"
          >
            <ol className="list-decimal space-y-3 pl-5 text-[#9CA3AF] text-sm font-body leading-relaxed">
              <li>
                Create a separate HighLevel workflow for Warm Up. Do not change the existing Digital Brain workflow named FractoLabs: Opportunity Stage Changed, and do not reuse its webhook URLs.
              </li>
              <li>
                Use the Pipeline Stage Changed trigger. Do not limit that trigger to one pipeline. It should send stage changes for both the turn-on pair and the turn-off pair, even when those pairs are in different pipelines.
              </li>
              <li>
                Add a Custom Webhook action that POSTs to the Warm Up webhook URL below.
                <label className="block space-y-1">
                  <span className="text-[#F3F4F6] text-xs font-body">Warm Up webhook URL</span>
                  <input
                    readOnly
                    value={webhookUrl}
                    className="w-full rounded-lg bg-[#0F1115] border border-white/10 px-3 py-2 text-sm text-[#F3F4F6] font-body"
                    data-testid="warm-up-workflow-url"
                  />
                </label>
              </li>
              <li>
                The Warm Up webhook URL is enough. Do not add an Authorization header. A Warm Up webhook secret is not required for this request.
              </li>
            </ol>
            <details className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
              <summary className="cursor-pointer text-sm text-[#9CA3AF] font-body select-none">
                Technical details
              </summary>
              <div className="mt-3">
                <p>Warm Up changes state only for an exact match with one saved pair.</p>
                <p>
                  Confirmed events already include these body fields: location.id, the opportunity id in id, contact_id, pipeline_id, and the exact stage name in pipleline_stage (spelled this way by HighLevel). customData on those events was empty, so do not add other field names for these values.
                </p>
                <p>
                  Confirmed events do not include a stage id. Match the exact pipeline_id and the exact stage name in pipleline_stage. Do not match a similar name, do not use pipeline_name as the stage, and do not use a catalog lookup. This match does not require the PIT key to be active.
                </p>
              </div>
            </details>
          </div>
        </details>
      </section>
      <section
        className="glass rounded-2xl p-6 border border-white/10 space-y-4 mb-6"
        data-testid="warm-up-advanced"
      >
        <h2 className="text-[#F3F4F6] font-heading font-semibold text-lg flex items-center gap-2">
          Advanced
        </h2>
        <p className="text-[#9CA3AF] text-sm font-body" data-testid="warm-up-secret-optional">
          Optional. Not needed for the HighLevel workflow above.
        </p>
        {pitKind === "loading" ? null : (
          <p className="text-[#9CA3AF] text-sm font-body" data-testid="warm-up-secret-state">
            {webhookSecretConfigured ? "A webhook secret is saved." : "No webhook secret is saved."}
          </p>
        )}
        <div className="flex items-center gap-3 flex-wrap">
          {!secret && !alreadyIssued ? (
            <button
              type="button"
              onClick={() => submit(false)}
              disabled={pending}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#00D1FF] text-[#0F1115] text-sm font-heading font-bold disabled:opacity-50"
              data-testid="warm-up-workflow-issue"
            >
              {pending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Create webhook secret
            </button>
          ) : (
            <button
              type="button"
              onClick={() => submit(true)}
              disabled={pending}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#00D1FF]/10 border border-[#00D1FF]/30 text-[#00D1FF] text-sm font-body disabled:opacity-50"
              data-testid="warm-up-workflow-rotate"
            >
              {pending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Replace webhook secret
            </button>
          )}
        </div>
        {secret ? (
          <label className="block space-y-1">
            <span className="text-[#F3F4F6] text-xs font-body">Warm Up webhook secret</span>
            <input
              readOnly
              value={secret}
              autoComplete="off"
              spellCheck={false}
              className="w-full rounded-lg bg-[#0F1115] border border-white/10 px-3 py-2 text-sm text-[#F3F4F6] font-body"
              data-testid="warm-up-workflow-secret"
            />
          </label>
        ) : null}
        {error ? (
          <p className="text-red-400 text-xs font-body" role="alert" data-testid="warm-up-workflow-error">
            {error}
          </p>
        ) : null}
        <WarmUpManualSendRule checking={pitKind === "loading"} />
      </section>
    </>
  );
}
